import type { MenuButton } from "@velizhanin-dev/bot-scenario"
import { useEffect, useState } from "react"
import { Button } from "@/shared/ui/button"
import { Field } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import { SegmentedControl } from "@/shared/ui/segmented-control"
import { Reveal } from "@/shared/ui/reveal"

type Kind = "keep" | MenuButton["kind"]

const OPTIONS = [
	{ value: "keep", label: "Как в BotFather" },
	{ value: "commands", label: "Список команд" },
	{ value: "web_app", label: "Мини-приложение" },
] as const

const isHttps = (value: string) => /^https:\/\/\S+\.\S+/.test(value.trim())

/**
 * Кнопка слева от поля ввода в чате с ботом. «Как в BotFather» ничего не меняет: так
 * кнопку, настроенную вручную, не сотрёт публикация.
 */
export function MenuButtonCard({
	value,
	onChange,
}: {
	value: MenuButton | undefined
	onChange: (value: MenuButton | undefined) => void
}) {
	const [kind, setKind] = useState<Kind>(value?.kind ?? "keep")
	const [label, setLabel] = useState(value?.kind === "web_app" ? value.label : "Открыть")
	const [url, setUrl] = useState(value?.kind === "web_app" ? value.url : "")
	useEffect(() => {
		setKind(value?.kind ?? "keep")
		if (value?.kind === "web_app") {
			setLabel(value.label)
			setUrl(value.url)
		}
	}, [value])

	const pick = (next: Kind) => {
		setKind(next)
		if (next === "keep") onChange(undefined)
		if (next === "commands") onChange({ kind: "commands" })
	}
	const dirty =
		kind === "web_app" &&
		(value?.kind !== "web_app" || value.label !== label.trim() || value.url !== url.trim())
	const valid = label.trim().length > 0 && isHttps(url)

	return (
		<section className="space-y-4 rounded-2xl border border-border bg-card p-5">
			<div>
				<h3 className="text-sm font-medium">Кнопка у поля ввода</h3>
				<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
					Синяя кнопка слева от поля, где человек пишет сообщение. Список команд покажет /menu и
					команды воронок, мини-приложение откроется поверх чата.
				</p>
			</div>
			<div>
				<SegmentedControl
					aria-label="Кнопка у поля ввода"
					options={OPTIONS}
					value={kind}
					onChange={(next) => next && pick(next)}
				/>
				{/* Поля мини-приложения раскрываются по высоте, а не возникают рывком. */}
				<Reveal show={kind === "web_app"} className="pt-4">
					<div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] sm:items-end">
						<Field label="Подпись" htmlFor="menu-button-label">
							<Input
								id="menu-button-label"
								placeholder="Например, Открыть клуб"
								maxLength={64}
								value={label}
								onChange={(event) => setLabel(event.target.value)}
							/>
						</Field>
						<Field label="Адрес мини-приложения" htmlFor="menu-button-url">
							<Input
								id="menu-button-url"
								inputMode="url"
								placeholder="https://"
								value={url}
								onChange={(event) => setUrl(event.target.value)}
							/>
						</Field>
						<Button
							type="button"
							className="rounded-xl"
							disabled={!dirty || !valid}
							onClick={() => onChange({ kind: "web_app", label: label.trim(), url: url.trim() })}
						>
							Сохранить
						</Button>
					</div>
				</Reveal>
			</div>
		</section>
	)
}
